import { useState } from "react";
import { Upload, X } from "lucide-react";
import { uploadImage } from "../../sdk/upload/upload";
import Loading from "../../shared/Loading";
import { useError } from "../../context/ErrorProvider";

const RecordBankTransaction = ({
  closeRecordBankTransaction,
  handleDisburseSuccess,
  disburseTransaction,
  setDisburseTransaction,
  loading,
}) => {
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  const validateForm = () => {
    const errors = {};
    if (!disburseTransaction.bank) errors.bank = "Bank is required";
    if (!disburseTransaction.accountNumber)
      errors.accountNumber = "Account number is required";
    if (!disburseTransaction.referenceNumber)
      errors.referenceNumber = "Reference number is required";
    if (!disburseTransaction.transactionAmount)
      errors.transactionAmount = "Transaction amount is required";
    if (!disburseTransaction.transactionDate)
      errors.transactionDate = "Transaction date is required";
    return errors;
  };

  const handleChange = (e) => {
    console.log(e.target.value);
    const { name, value } = e.target;
    setDisburseTransaction((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const [uploadedFile, setUploadedFile] = useState(null);
  const { showError } = useError();

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadedFile(file);

    try {
      const response = await uploadImage(file);
      setDisburseTransaction((prev) => ({
        ...prev,
        receiptUrl: response.url,
      }));
    } catch (error) {
      console.error("Upload failed:", error);
    }
  };

  const handleNext = () => {
    const errors = validateForm();
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    if (!isConfirmed) {
      showError("Please confirm that funds have been disbursed offline");
      return;
    }

    handleDisburseSuccess();
  };

  return (
    <div
      onClick={closeRecordBankTransaction}
      className="fixed inset-0 bg-black/50 z-40 flex items-center justify-center"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-[900px] h-[620px] rounded-[10px] py-[20px] px-[40px] relative bg-white"
      >
        <div
          onClick={closeRecordBankTransaction}
          className="absolute top-0 cursor-pointer h-[40px] w-[40px] bg-[#D63B3B] rounded-tr-[10px] rounded-bl-[20px] right-0 flex items-center justify-center"
        >
          <X color="#ffffff" />
        </div>

        <div>
          <p className="text-[#000000] font-semibold text-[20px]">
            Record Disbursement - Bank
          </p>
          <p className="text-[#5C5C5C] font-normal text-[14px]">
            Enter the details of the disbursement you completed offline. Include
            transaction method, reference number, amount, and date to keep the
            loan record accurate.
          </p>

          {/* Bank & Account Number */}
          <div className="flex items-center mt-[10px] justify-between">
            <div className="flex w-[49%] flex-col">
              <label
                className="text-[#222222] font-medium text-[13px]"
                htmlFor="bank"
              >
                Bank
              </label>
              <input
                name="bank"
                placeholder="e.g Equity Bank"
                type="text"
                value={disburseTransaction.bank}
                onChange={handleChange}
                className="h-[45px] w-full text-[14px] rounded-[5px] px-[10px] border border-[#D9D9D9] focus:outline-none focus:ring-0 focus:ring-offset-0 focus:border-[#D9D9D9]"
              />
              {formErrors.bank && (
                <p className="text-red-500 text-sm mt-1">{formErrors.bank}</p>
              )}
            </div>
            <div className="flex w-[49%] flex-col">
              <label
                className="text-[#222222] font-medium text-[13px]"
                htmlFor="accountNumber"
              >
                Bank Account Number
              </label>
              <input
                name="accountNumber"
                placeholder="e.g 011000000000"
                type="text"
                value={disburseTransaction.accountNumber}
                onChange={handleChange}
                className="h-[45px] w-full text-[14px] rounded-[5px] px-[10px] border border-[#D9D9D9] focus:outline-none focus:ring-0 focus:ring-offset-0 focus:border-[#D9D9D9]"
              />
              {formErrors.accountNumber && (
                <p className="text-red-500 text-sm mt-1">
                  {formErrors.accountNumber}
                </p>
              )}
            </div>
          </div>

          {/* Reference & Amount */}
          <div className="flex items-center my-[10px] justify-between">
            <div className="flex w-[49%] flex-col">
              <label
                className="text-[#222222] font-medium text-[13px]"
                htmlFor="referenceNumber"
              >
                Reference Number
              </label>
              <input
                name="referenceNumber"
                placeholder="e.g ABC123456"
                type="text"
                value={disburseTransaction.referenceNumber}
                onChange={handleChange}
                className="h-[45px] w-full text-[14px] rounded-[5px] px-[10px] border border-[#D9D9D9] focus:outline-none focus:ring-0 focus:ring-offset-0 focus:border-[#D9D9D9]"
              />
              {formErrors.referenceNumber && (
                <p className="text-red-500 text-sm mt-1">
                  {formErrors.referenceNumber}
                </p>
              )}
            </div>
            <div className="flex w-[49%] flex-col">
              <label
                className="text-[#222222] font-medium text-[13px]"
                htmlFor="transactionAmount"
              >
                Transaction Amount (KES)
              </label>
              <input
                name="transactionAmount"
                placeholder="e.g 200,000"
                type="number"
                value={disburseTransaction.transactionAmount}
                onChange={handleChange}
                className="h-[45px] w-full text-[14px] rounded-[5px] px-[10px] border border-[#D9D9D9] focus:outline-none focus:ring-0 focus:ring-offset-0 focus:border-[#D9D9D9]"
              />
              {formErrors.transactionAmount && (
                <p className="text-red-500 text-sm mt-1">
                  {formErrors.transactionAmount}
                </p>
              )}
            </div>
          </div>

          {/* Transaction Date */}
          <div className="flex items-center my-[10px] justify-between">
            <div className="flex w-[49%] flex-col">
              <label
                className="text-[#222222] font-medium text-[13px]"
                htmlFor="transactionDate"
              >
                Transaction Date
              </label>
              <input
                type="date"
                name="transactionDate"
                id="transactionDate"
                value={disburseTransaction.transactionDate}
                onChange={handleChange}
                className="h-[45px] w-full text-[14px] rounded-[5px] px-[10px] border border-[#D9D9D9] focus:outline-none focus:ring-0 focus:border-[#D9D9D9]"
              />
              {formErrors.transactionDate && (
                <p className="text-red-500 text-sm mt-1">
                  {formErrors.transactionDate}
                </p>
              )}
            </div>
          </div>

          {/* Receipt Upload */}
          <div>
            <p className="text-[#000000] font-normal text-[13px]">
              Upload Transaction Receipt
            </p>
            <div className="border border-[#A8A8A8] rounded-[5px] w-[45%] bg-white p-[10px] flex flex-col justify-center items-center relative">
              <Upload size={40} />
              <p className="text-[12px] font-normal text-[#000000]">
                Drag and Drop file here or{" "}
                <label className="text-[#000000] cursor-pointer">
                  Choose file
                  <input
                    type="file"
                    className="hidden"
                    accept="image/*,.pdf"
                    onChange={handleFileChange}
                  />
                </label>
              </p>
              {uploadedFile && (
                <div className="mt-3 text-center">
                  <p className="text-sm text-[#000000] font-medium">
                    {uploadedFile.name}
                  </p>
                  <p className="text-xs text-[#5C5C5C]">
                    {(uploadedFile.size / 1024).toFixed(2)} KB
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Confirmation Checkbox */}
        <div className="absolute bottom-[100px] left-[40px] flex items-start gap-[8px] right-[20px]">
          <input
            type="checkbox"
            name="confirm"
            id="confirm"
            checked={isConfirmed}
            onChange={(e) => setIsConfirmed(e.target.checked)}
          />
          <p className="text-[#5C5C5C] text-[10px] font-normal">
            I confirm that the funds have been disbursed offline and that the
            details provided are accurate and reflect a true record of the
            transaction.
          </p>
        </div>

        {/* Buttons */}
        <div className="absolute bottom-[20px] right-[20px] flex items-center justify-center gap-[10px]">
          <button
            onClick={closeRecordBankTransaction}
            className="h-[35px] bg-[#F3F3F3] rounded-[30px] px-[15px] text-[#5C5C5C] font-medium"
          >
            Close
          </button>
          <button
            disabled={loading}
            onClick={handleNext}
            className="h-[35px] bg-[#000000] flex items-center justify-center gap-[5px] rounded-[30px] px-[15px] text-[#FFFFFF] font-medium"
          >
            {loading && <Loading />}
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default RecordBankTransaction;
